import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { logout } from '../../store/slices/auth.slice';
import { toggleSidebar, setTheme, setLanguage, setSidebarOpen } from '../../store/slices/ui.slice';
import GlobalSearch from '../common/GlobalSearch';
import ApplicationSelector from '../common/ApplicationSelector';
import PlatformDots from './PlatformDots';
import alertInstanceService from '../../services/api/alert-instance.service';

// Metric labels for translation
const metricLabels: Record<string, { en: string; pt: string }> = {
  'response_time': { en: 'Response time', pt: 'Tempo de resposta' },
  'response_time_avg': { en: 'Avg response time', pt: 'Tempo médio de resposta' },
  'error_rate': { en: 'Error rate', pt: 'Taxa de erros' },
  'throughput': { en: 'Throughput', pt: 'Throughput' },
  'request_count': { en: 'Request count', pt: 'Quantidade de requisições' },
  'latency': { en: 'Latency', pt: 'Latência' },
  'latency_p99': { en: 'P99 Latency', pt: 'Latência P99' },
  'latency_p95': { en: 'P95 Latency', pt: 'Latência P95' },
  'cpu_usage': { en: 'CPU usage', pt: 'Uso de CPU' },
  'memory_usage': { en: 'Memory usage', pt: 'Uso de memória' },
  'disk_usage': { en: 'Disk usage', pt: 'Uso de disco' },
};

// Format alert message with i18n support
const formatAlertMessage = (message: string | undefined | null, lang: string = 'pt'): string => {
  if (!message) return '';

  // Handle structured anomaly format
  if (message.startsWith('anomaly:')) {
    const parts = message.split('|');
    const type = parts[0].replace('anomaly:', '');

    if (type === 'new_activity' && parts.length >= 4) {
      const [, metric, value, unit] = parts;
      const metricLabel = metricLabels[metric]?.[lang as 'en' | 'pt'] || metric;
      return lang === 'pt'
        ? `${metricLabel}: nova atividade (${value}${unit})`
        : `${metricLabel}: new activity (${value}${unit})`;
    }

    if (type === 'deviation' && parts.length >= 7) {
      const [, metric, current, , deviation, unit, direction] = parts;
      const metricLabel = metricLabels[metric]?.[lang as 'en' | 'pt'] || metric;
      const dirWord = lang === 'pt'
        ? (direction === 'above' ? '↑' : '↓')
        : (direction === 'above' ? '↑' : '↓');

      return `${metricLabel} ${dirWord}${deviation}% (${current}${unit})`;
    }
  }

  // Clean up old format
  return message
    .replace(/\s*\(expected:\s*0\.0,\s*deviation:\s*0\.0%\)/gi, '')
    .replace(/\s*\(expected:\s*0,\s*deviation:\s*0%\)/gi, '')
    .trim();
};

const LANGUAGES = [
  { code: 'pt-BR', flag: '🇧🇷', label: 'PT' },
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'de', flag: '🇩🇪', label: 'DE' },
  { code: 'it', flag: '🇮🇹', label: 'IT' },
  { code: 'pl', flag: '🇵🇱', label: 'PL' },
  { code: 'da', flag: '🇩🇰', label: 'DA' },
  { code: 'fil', flag: '🇵🇭', label: 'FIL' },
  { code: 'zh', flag: '🇨🇳', label: 'ZH' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const currentLanguage =
    LANGUAGES.find((l) => l.code === i18n.language) ||
    LANGUAGES.find((l) => i18n.language?.startsWith(l.code.split('-')[0])) ||
    LANGUAGES[1];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const theme = useSelector((state: RootState) => state.ui.theme);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [firingCount, setFiringCount] = useState(0);

  // Detectar scroll para adicionar shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-user-menu]') && !target.closest('[data-notifications]') && !target.closest('[data-lang-menu]')) {
        setShowUserMenu(false);
        setShowNotifications(false);
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply dark mode class on initial load and when theme changes
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const handleLogout = () => {
    dispatch(logout());

    // Limpar todos os caches e estado da aplicação
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Limpar sessionStorage
    sessionStorage.clear();

    // Usar window.location para forçar reload completo da aplicação
    window.location.href = '/login';
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    dispatch(setTheme(newTheme));
    // The useEffect above will handle applying the class
  };

  const changeLanguage = (lang: string) => {
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  // Load recent alerts (all statuses, firing first) and total firing count
  const loadRecentAlerts = async () => {
    try {
      setAlertsLoading(true);
      const [alertsResponse, statsResponse] = await Promise.all([
        alertInstanceService.getAll({}, 10, 0),
        alertInstanceService.getStats(),
      ]);

      if (alertsResponse.success) {
        // Sort to show firing alerts first, then by triggered_at
        const sorted = alertsResponse.data.sort((a: any, b: any) => {
          // Firing alerts come first
          if (a.status === 'firing' && b.status !== 'firing') return -1;
          if (a.status !== 'firing' && b.status === 'firing') return 1;
          // Then sort by triggered_at (most recent first)
          return new Date(b.triggered_at).getTime() - new Date(a.triggered_at).getTime();
        });
        // Deduplica alertas repetidos (mesma regra+app) p/ não poluir o dropdown
        const seen = new Set<string>();
        const deduped = sorted.filter((a: any) => {
          const key = `${a.alert_rule_id || a.alert_rule?.name || ''}|${a.application_id || ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        // Show up to 4 most recent (deduplicated) alerts
        setRecentAlerts(deduped.slice(0, 4));
      }

      if (statsResponse.success) {
        setFiringCount(statsResponse.data.firing || 0);
      }
    } catch (error) {
      console.error('Error loading recent alerts:', error);
      setRecentAlerts([]);
      setFiringCount(0);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Auto-refresh alerts every 30 seconds
  useEffect(() => {
    // Load immediately
    loadRecentAlerts();

    // Then poll every 30 seconds
    const interval = setInterval(() => {
      loadRecentAlerts();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Marcar todas como lidas (acknowledge) de uma vez
  const [ackingAll, setAckingAll] = useState(false);
  const handleAcknowledgeAll = async () => {
    try {
      setAckingAll(true);
      await alertInstanceService.acknowledgeAll();
      await loadRecentAlerts();
    } catch (error) {
      console.error('Error acknowledging all alerts:', error);
    } finally {
      setAckingAll(false);
    }
  };

  // Calculate relative time
  const getRelativeTime = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} min`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  // Get icon path for alert severity
  const getAlertIcon = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'M6 18L18 6M6 6l12 12';
      case 'warning':
        return 'M12 9v2m0 4h.01';
      default:
        return 'M13 16h-1v-4h-1m1-4h.01';
    }
  };

  // Get color for alert severity
  const getAlertColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'red';
      case 'warning':
        return 'yellow';
      default:
        return 'blue';
    }
  };

  // Breadcrumb gerado dinamicamente
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    if (paths.length === 0) return [{ name: 'Dashboard', path: '/dashboard' }];

    // UUID regex pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Map of context-specific labels for UUIDs
    const contextLabels: { [key: string]: string } = {
      'funnels': 'Detalhes do Funil',
      'cohorts': 'Detalhes da Coorte',
      'users': 'Detalhes do Usuário',
      'applications': 'Detalhes da Aplicação',
      'alerts': 'Detalhes do Alerta',
      'dashboards': 'Detalhes do Dashboard',
    };

    return paths.map((path, index) => {
      // Check if this path segment is a UUID
      if (uuidPattern.test(path)) {
        // Look at the previous segment to determine context
        const previousPath = index > 0 ? paths[index - 1] : null;
        const contextLabel = previousPath && contextLabels[previousPath]
          ? contextLabels[previousPath]
          : 'Detalhes';

        return {
          name: contextLabel,
          path: '/' + paths.slice(0, index + 1).join('/'),
        };
      }

      // Normal path segment - capitalize and format
      return {
        name: path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' '),
        path: '/' + paths.slice(0, index + 1).join('/'),
      };
    });
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-40 border-b bg-white/80 backdrop-blur-xl dark:bg-gray-900/80 lg:left-64 transition-all duration-300 ${
        scrolled
          ? 'border-gray-300/50 dark:border-gray-700/50 shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50'
          : 'border-gray-200/50 dark:border-gray-800/50'
      }`}
    >
      <div className="flex h-16 items-center justify-between px-4 sm:px-6">
        {/* Left: Mobile menu + Breadcrumb */}
        <div className="flex flex-1 items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="rounded-xl p-2 text-gray-500 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 dark:text-gray-400 dark:hover:from-gray-800 dark:hover:to-gray-800/50 lg:hidden transition-all duration-200 hover:shadow-md"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Breadcrumb removido: cada página já tem seu próprio título/voltar. */}
        </div>

        {/* Center: Search (hidden on small screens) */}
        <div className="hidden lg:block flex-1 max-w-xl mx-4">
          <GlobalSearch />
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {/* Aplicação (contexto) */}
          <ApplicationSelector />

          <div className="mx-0.5 hidden h-6 w-px bg-gray-200 dark:bg-gray-800 sm:block" />

          {/* Cluster de utilidades */}
          <div className="flex items-center gap-0.5">
            {/* Switcher de produtos */}
            <PlatformDots current="analytics" />

          {/* Theme Toggle - Hidden on mobile */}
          <button
            onClick={toggleTheme}
            className="hidden h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200 sm:flex"
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            <div className="relative w-5 h-5">
              <svg
                className={`absolute inset-0 transition-all duration-300 ${theme === 'dark' ? 'opacity-100 rotate-0' : 'opacity-0 rotate-180'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <svg
                className={`absolute inset-0 transition-all duration-300 ${theme === 'dark' ? 'opacity-0 -rotate-180' : 'opacity-100 rotate-0'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </div>
          </button>

          {/* Language Switcher */}
          <div className="relative" data-lang-menu>
            <button
              onClick={() => { setShowLangMenu(!showLangMenu); setShowNotifications(false); setShowUserMenu(false); }}
              className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              title="Idioma"
            >
              <span className="text-base leading-none">{currentLanguage.flag}</span>
              <span className="hidden text-sm font-medium sm:inline">{currentLanguage.label}</span>
              <svg className={`h-3.5 w-3.5 transition-transform duration-200 ${showLangMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showLangMenu && (
              <div className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-gray-200/80 bg-white/95 py-1 shadow-2xl backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-800/95 animate-scale-in">
                {LANGUAGES.map((lang) => {
                  const active = i18n.language === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => { changeLanguage(lang.code); setShowLangMenu(false); }}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors ${
                        active
                          ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <span className="text-base leading-none">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.label}</span>
                      {active && (
                        <svg className="h-4 w-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="relative" data-notifications>
            <button
              onClick={() => {
                // On mobile (< 768px), navigate directly to alerts page
                if (window.innerWidth < 768) {
                  // Close sidebar on mobile
                  dispatch(setSidebarOpen(false));
                  navigate('/alerts');
                  return;
                }
                // On desktop, show dropdown
                const newShowState = !showNotifications;
                setShowNotifications(newShowState);
                setShowUserMenu(false);
                if (newShowState) {
                  loadRecentAlerts();
                }
              }}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {firingCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg">
                  {firingCount > 99 ? '99+' : firingCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-3 w-96 rounded-xl border border-gray-200/80 bg-white/95 backdrop-blur-xl shadow-2xl dark:border-gray-700/80 dark:bg-gray-800/95 overflow-hidden animate-scale-in">
                <div className="border-b border-gray-200/80 px-4 py-3 dark:border-gray-700/80 flex items-center justify-between bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span>{t('notifications', 'Notifications')}</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    {firingCount > 0 && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg">
                        {firingCount > 99 ? '99+' : firingCount}
                      </span>
                    )}
                    {firingCount > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAcknowledgeAll(); }}
                        disabled={ackingAll}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                        title={t('markAllRead', 'Marcar todas como lidas')}
                      >
                        {ackingAll ? (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-indigo-600 dark:border-indigo-400" />
                        ) : (
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        <span className="hidden sm:inline">{t('markAllRead', 'Marcar todas como lidas')}</span>
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                  {alertsLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : recentAlerts.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('noActiveAlerts', 'Nenhum alerta ativo')}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                      {recentAlerts.map((alert: any) => {
                        const severity = alert.alert_rule?.severity || 'warning';
                        const alertColor = getAlertColor(severity);
                        return (
                          <div
                            key={alert.id}
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/alerts');
                            }}
                            className="px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 cursor-pointer transition-all duration-200 group"
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-${alertColor}-100 dark:bg-${alertColor}-900/30 group-hover:scale-110 transition-transform duration-200`}>
                                <svg className={`h-4 w-4 text-${alertColor}-600 dark:text-${alertColor}-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getAlertIcon(severity)} />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{alert.alert_rule?.name || 'Alert'}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{formatAlertMessage(alert.message, currentLang) || (currentLang === 'pt' ? 'Alerta disparado' : 'Alert triggered')}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {alert.triggered_at ? getRelativeTime(alert.triggered_at) : 'Just now'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200/80 dark:border-gray-700/80 px-4 py-3 bg-gray-50/50 dark:bg-gray-800/50">
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate('/alerts');
                    }}
                    className="w-full text-center text-sm font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors duration-200"
                  >
                    {t('viewAllAlerts', 'View All Alerts')} →
                  </button>
                </div>
              </div>
            )}
          </div>

          </div>{/* fim do cluster de utilidades */}

          <div className="mx-0.5 h-6 w-px bg-gray-200 dark:bg-gray-800" />

          {/* User Menu */}
          <div className="relative" data-user-menu>
            <button
              onClick={() => {
                setShowUserMenu(!showUserMenu);
                setShowNotifications(false);
                setShowLangMenu(false);
              }}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 group"
            >
              {(user as any)?.avatarUrl ? (
                <img src={(user as any).avatarUrl} alt="Avatar" className="h-8 w-8 rounded-lg object-cover shadow-lg shadow-indigo-500/30 transition-transform duration-200 group-hover:scale-110" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-sm font-bold text-white shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform duration-200">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
              )}
              <div className="hidden text-left sm:block">
                <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  {user?.firstName} {user?.lastName}
                  {user?.isSuperAdmin === true && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg">
                      ADMIN
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{user?.roles?.[0] || 'User'}</div>
              </div>
              <svg className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-3 w-64 rounded-xl border border-gray-200/80 bg-white/95 backdrop-blur-xl shadow-2xl dark:border-gray-700/80 dark:bg-gray-800/95 overflow-hidden animate-scale-in">
                <div className="border-b border-gray-200/80 px-4 py-3 dark:border-gray-700/80 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50">
                  <div className="flex items-center space-x-3 mb-2">
                    {(user as any)?.avatarUrl ? (
                      <img src={(user as any).avatarUrl} alt="Avatar" className="h-10 w-10 rounded-lg object-cover shadow-lg" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-sm font-bold text-white shadow-lg">
                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      {user?.isSuperAdmin === true && (
                        <span className="inline-block px-2 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                          ADMIN
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/settings/profile');
                      setShowUserMenu(false);
                    }}
                    className="flex w-full items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 dark:text-gray-300 dark:hover:from-gray-700 dark:hover:to-gray-700/50 transition-all duration-200 group"
                  >
                    <svg className="mr-3 h-4 w-4 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('profile', 'Profile')}
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowUserMenu(false);
                    }}
                    className="flex w-full items-center px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 dark:text-gray-300 dark:hover:from-gray-700 dark:hover:to-gray-700/50 transition-all duration-200 group"
                  >
                    <svg className="mr-3 h-4 w-4 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t('settings', 'Settings')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center border-t border-gray-200/80 dark:border-gray-700/80 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 dark:text-red-400 dark:hover:from-red-900/20 dark:hover:to-rose-900/20 transition-all duration-200 group"
                  >
                    <svg className="mr-3 h-4 w-4 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {t('buttons.logout', 'Logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
